import { CACHE_STATUS_FRESH, createCacheClient } from '@noths/polaris-server-caching';

import type { ContentWithStatus } from '../types';
import { fetchContentGraphQLWithoutCaching } from './fetchContentGraphQLWithoutCaching';
import type { FetchContentGraphQlArgs, FetchContentGraphQlOptions } from './types';

const fetchPromises: Partial<Record<string, Promise<ContentWithStatus>>> = {};

interface FetchContentGraphQLWithCachingOptions
  extends Omit<FetchContentGraphQlOptions, 'cacheKey' | 'cacheTTLInSeconds'> {
  // these are mandatory for this function
  cacheKey: string;
  cacheTTLInSeconds: number;
}

export const fetchContentGraphQLWithCaching = async (
  { deliveryToken, environment, query, stackAPIKey, variables }: FetchContentGraphQlArgs,
  { branch, cacheKey, cacheTTLInSeconds, region, timeout }: FetchContentGraphQLWithCachingOptions,
): Promise<ContentWithStatus> => {
  const fetchFn = () =>
    fetchContentGraphQLWithoutCaching(
      { deliveryToken, environment, query, stackAPIKey, variables },
      { branch, region, timeout },
    );

  const cacheClient = createCacheClient();

  // cacheClient will be undefined if REDIS env vars are missing or invalid
  const cachedEntry = await cacheClient?.getByKey(cacheKey, cacheTTLInSeconds);

  if (cachedEntry && cachedEntry.status === CACHE_STATUS_FRESH) {
    // cache hit
    return Promise.resolve({
      content: cachedEntry.data,
      status: 'fresh',
    });
  }

  const fetchInProgress = fetchPromises[cacheKey];

  if (fetchInProgress) {
    // fetch in progress

    return fetchInProgress;
  }

  // cache miss or expired
  const fetchPromise = fetchFn()
    .then((content): ContentWithStatus => {
      delete fetchPromises[cacheKey];
      cacheClient?.setByKey(cacheKey, content);

      return {
        content: content,
        status: 'fresh',
      };
    })
    .catch((error): ContentWithStatus => {
      delete fetchPromises[cacheKey];

      if (cachedEntry) {
        return {
          content: cachedEntry.data,
          status: 'expired',
        };
      } else {
        return {
          content: error,
          status: 'error',
        };
      }
    });

  fetchPromises[cacheKey] = fetchPromise;

  return fetchPromise;
};
